export type AssessmentQuestionKind = "boolean" | "single" | "multi" | "enum";

export type AssessmentMode = "standard" | "adult_compliance";

export type AssessmentOption = {
  readonly id: string;
  readonly label: string;
  readonly description?: string;
  readonly score: number;
  readonly booleanValue?: boolean;
  readonly flags?: readonly string[];
  readonly nextStepIds?: readonly string[];
};

export type AssessmentQuestion = {
  readonly id: string;
  readonly label: string;
  readonly description?: string;
  readonly kind: AssessmentQuestionKind;
  readonly required?: boolean;
  readonly options: readonly AssessmentOption[];
};

export type AssessmentTier = {
  readonly id: string;
  readonly label: string;
  readonly summary: string;
  readonly minScore: number;
  readonly maxScore: number;
  readonly statusBadge?: string;
  readonly nextStepIds?: readonly string[];
};

export type AssessmentNextStep = {
  readonly id: string;
  readonly label: string;
  readonly description?: string;
  readonly ctaLabel?: string;
  readonly href?: string;
};

export type AssessmentSchema = {
  readonly id: string;
  readonly mode?: AssessmentMode;
  readonly questions: readonly AssessmentQuestion[];
  readonly tiers: readonly AssessmentTier[];
  readonly nextSteps?: readonly AssessmentNextStep[];
};

export type AssessmentAnswerValue = string | readonly string[] | boolean | null | undefined;

export type AssessmentResponses = Readonly<Record<string, AssessmentAnswerValue>>;

export type AssessmentQuestionResult = {
  readonly questionId: string;
  readonly answered: boolean;
  readonly selectedOptionIds: readonly string[];
  readonly score: number;
  readonly maxScore: number;
  readonly flags: readonly string[];
  readonly nextStepIds: readonly string[];
};

export type AssessmentProgress = {
  readonly answered: number;
  readonly total: number;
  readonly requiredAnswered: number;
  readonly requiredTotal: number;
  readonly percent: number;
  readonly complete: boolean;
};

export type AssessmentResult = {
  readonly schemaId: string;
  readonly mode: AssessmentMode;
  readonly tier: AssessmentTier;
  readonly score: number;
  readonly maxScore: number;
  readonly scorePercent: number;
  readonly progress: AssessmentProgress;
  readonly questionResults: readonly AssessmentQuestionResult[];
  readonly flags: readonly string[];
  readonly topGaps: readonly AssessmentOption[];
  readonly nextSteps: readonly AssessmentNextStep[];
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const unique = <T>(values: readonly T[]): T[] => [...new Set(values)];

function selectedIdsFor(question: AssessmentQuestion, answer: AssessmentAnswerValue): string[] {
  if (answer === undefined || answer === null) {
    return [];
  }

  if (question.kind === "multi") {
    return Array.isArray(answer) ? unique(answer.map(String).filter(Boolean)) : [];
  }

  if (typeof answer === "boolean") {
    const matchingOption = question.options.find((option) => option.booleanValue === answer);
    return matchingOption ? [matchingOption.id] : [];
  }

  const id = String(answer).trim();
  return id ? [id] : [];
}

function maxScoreFor(question: AssessmentQuestion): number {
  if (question.kind === "multi") {
    return question.options.reduce((sum, option) => sum + Math.max(0, option.score), 0);
  }

  return Math.max(0, ...question.options.map((option) => option.score));
}

function resultForQuestion(question: AssessmentQuestion, responses: AssessmentResponses): AssessmentQuestionResult {
  const selectedOptionIds = selectedIdsFor(question, responses[question.id]);
  const selectedOptions = question.options.filter((option) => selectedOptionIds.includes(option.id));
  const score = selectedOptions.reduce((sum, option) => sum + option.score, 0);
  const maxScore = maxScoreFor(question);

  return {
    questionId: question.id,
    answered: selectedOptionIds.length > 0,
    selectedOptionIds,
    score: clamp(score, 0, maxScore),
    maxScore,
    flags: unique(selectedOptions.flatMap((option) => option.flags ?? [])),
    nextStepIds: unique(selectedOptions.flatMap((option) => option.nextStepIds ?? [])),
  };
}

export function getAssessmentProgress(schema: AssessmentSchema, responses: AssessmentResponses): AssessmentProgress {
  const questionResults = schema.questions.map((question) => resultForQuestion(question, responses));
  const answered = questionResults.filter((result) => result.answered).length;
  const requiredQuestions = schema.questions.filter((question) => question.required !== false);
  const requiredAnswered = requiredQuestions.filter((question) => resultForQuestion(question, responses).answered).length;
  const total = schema.questions.length;
  const requiredTotal = requiredQuestions.length;

  return {
    answered,
    total,
    requiredAnswered,
    requiredTotal,
    percent: total > 0 ? Math.round((answered / total) * 100) : 0,
    complete: requiredAnswered >= requiredTotal,
  };
}

function tierFor(schema: AssessmentSchema, scorePercent: number): AssessmentTier {
  const sortedTiers = [...schema.tiers].sort((a, b) => a.minScore - b.minScore);
  const tier = sortedTiers.find((candidate) => scorePercent >= candidate.minScore && scorePercent <= candidate.maxScore);

  return (
    tier ??
    sortedTiers.at(-1) ?? {
      id: "unclassified",
      label: "",
      summary: "",
      minScore: 0,
      maxScore: 100,
    }
  );
}

function topGapsFor(schema: AssessmentSchema, questionResults: readonly AssessmentQuestionResult[], limit: number): AssessmentOption[] {
  const resultByQuestion = new Map(questionResults.map((result) => [result.questionId, result]));

  return schema.questions
    .flatMap((question) => {
      const result = resultByQuestion.get(question.id);
      if (!result || result.score >= result.maxScore) {
        return [];
      }

      const bestUnselected = [...question.options]
        .filter((option) => !result.selectedOptionIds.includes(option.id))
        .sort((a, b) => b.score - a.score)
        .find((option) => option.score > 0);

      return bestUnselected ? [bestUnselected] : [];
    })
    .slice(0, limit);
}

export function evaluateAssessment(schema: AssessmentSchema, responses: AssessmentResponses): AssessmentResult {
  const questionResults = schema.questions.map((question) => resultForQuestion(question, responses));
  const score = questionResults.reduce((sum, result) => sum + result.score, 0);
  const maxScore = questionResults.reduce((sum, result) => sum + result.maxScore, 0);
  const scorePercent = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
  const tier = tierFor(schema, scorePercent);
  const nextStepIds = unique([
    ...(tier.nextStepIds ?? []),
    ...questionResults.flatMap((result) => result.nextStepIds),
  ]);
  const nextSteps = (schema.nextSteps ?? []).filter((step) => nextStepIds.includes(step.id));

  return {
    schemaId: schema.id,
    mode: schema.mode ?? "standard",
    tier,
    score,
    maxScore,
    scorePercent,
    progress: getAssessmentProgress(schema, responses),
    questionResults,
    flags: unique(questionResults.flatMap((result) => result.flags)),
    topGaps: topGapsFor(schema, questionResults, 4),
    nextSteps,
  };
}
